import React from "react";

import { MapPin, Users, Store, Building, ShieldCheck } from "lucide-react";

const Mudhalvar = () => {
  return (
    <div className="min-h-screen bg-white font-sans">
   
{/* Top Header Strip */}
<div className="w-full bg-white shadow-sm py-6 px-6 flex items-center justify-center gap-3">
  <Users className="w-10 h-10 text-blue-600" />

  <h1 className="text-lg sm:text-xl font-semibold text-gray-800">
    Mudhalvar Marundhagam
  </h1>
</div>



      {/* Top Banner with Image */}
      <header className="w-full">
        <div className="relative w-full h-74 bg-gradient-to-r from-cyan-500 to-blue-600">
          <img
            src="/Images/Mudhalver_marundhagam.png"
            alt="Mudhalvar Maruthnagam Banner"
            className="w-full h-full object-cover"
          />
        </div>
      </header>

{/* Overview Section */}
<section className="w-full px-6 sm:px-10 lg:px-20 py-12 bg-blue-100">
  <h2 className="text-3xl font-bold mb-6 text-gray-900">Overview</h2>

  {/* Main Overview Text Box */}
  <div className="rounded-lg p-6 pl-[10px] border-l-4 border-blue-500 w-full">
    <p className="text-gray-700 leading-relaxed text-lg">
      Mudhalvar Marundhagam is a bold initiative by the Tamil Nadu government to make essential medicines
      affordable and accessible to all. Through a network of state-backed pharmacies, this scheme offers
      generic—and where required, branded and traditional medicines—at significantly subsidised prices,
      reducing the burden of healthcare costs for individuals and families throughout the state.
    </p>
  </div>
</section>


{/* Quote Section (Separate) */}
<section className="w-full flex justify-center px-6 sm:px-10 lg:px-20 py-10 bg-white">
  <div className=" p-6 rounded-lg border-2 border-dotted border-blue-400 shadow-sm w-[60%] bg-white">
    <p className="text-gray-700 text-base italic text-center leading-relaxed">
      To build a statewide network of affordable, high-quality pharmacies that reduce healthcare costs,
      support public health, and ensure that every citizen—regardless of income or location—has access
      to essential medicines without financial hardship.
    </p>
  </div>
</section>

{/* What We Offer Section */}
<section className="w-full px-6 sm:px-10 lg:px-20 py-16 bg-gray-100">

  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-7xl mx-auto">

    {/* Card 1 */}
    <div className="bg-blue-600 text-white rounded-xl p-8 shadow-md text-center hover:-translate-y-1 transition">
     
      <h3 className="text-xl font-semibold mb-3">Affordable Medicines</h3>
      <p className="text-sm opacity-90">Essential medicines at subsidised prices.</p>
    </div>

    {/* Card 2 */}
    <div className="bg-cyan-600 text-white rounded-xl p-8 shadow-md text-center hover:-translate-y-1 transition">
    
      <h3 className="text-xl font-semibold mb-3">OTC & Wellness</h3>
      <p className="text-sm opacity-90">Daily healthcare & wellness essentials.</p>
    </div>

    {/* Card 3 */}
    <div className="bg-teal-600 text-white rounded-xl p-8 shadow-md text-center hover:-translate-y-1 transition">
      
      <h3 className="text-xl font-semibold mb-3">Chronic Care</h3>
      <p className="text-sm opacity-90">Support for long-term health conditions.</p>
    </div>

    {/* Card 4 */}
    <div className="bg-gray-700 text-white rounded-xl p-8 shadow-md text-center hover:-translate-y-1 transition">
      
      <h3 className="text-xl font-semibold mb-3">Traditional Medicines</h3>
      <p className="text-sm opacity-90">Siddha, Ayurveda & Unani essentials.</p>
    </div>

    {/* Card 5 */}
    <div className="bg-blue-900 text-white rounded-xl p-8 shadow-md text-center hover:-translate-y-1 transition">
    
      <h3 className="text-xl font-semibold mb-3">Health Equipment</h3>
      <p className="text-sm opacity-90">BP monitors, glucometers & safety items.</p>
    </div>

  </div>
</section>


    {/* How It Works Section */}
<section className="w-full px-6 sm:px-10 lg:px-20 py-16 bg-gray-50">
  <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center text-gray-900">
    How It <span className="text-blue-600">Works</span>
  </h2>

  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
    {[
      {
        icon: <Store className="w-8 h-8 text-white" />,
        title: "Procurement",
        description: "Sourcing high-quality products efficiently from trusted suppliers."
      },
      {
        icon: <Building className="w-8 h-8 text-white" />,
        title: "District Warehouses",
        description: "Organized storage at district level for quick distribution."
      },
      {
        icon: <MapPin className="w-8 h-8 text-white" />,
        title: "Outlets",
        description: "Local outlets ensuring products are accessible to everyone."
      },
      {
        icon: <Users className="w-8 h-8 text-white" />,
        title: "Citizens",
        description: "End-users benefit from timely delivery and quality service."
      }
    ].map((step, idx) => (
      <div
        key={idx}
        className="bg-white rounded-xl p-6 shadow-md text-center transform transition duration-300 hover:-translate-y-2 hover:shadow-lg"
      >
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-blue-400 to-cyan-400 transition-transform duration-300 hover:scale-110">
          {step.icon}
        </div>
        <h3 className="font-semibold text-gray-900 mb-2 text-lg">{step.title}</h3>
        <p className="text-gray-600 text-sm">{step.description}</p>
      </div>
    ))}
  </div>
</section>
{/* Key Stats Section */}
<section className="w-full px-6 sm:px-10 lg:px-20 py-16 bg-blue-50 ">
  <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center text-gray-900 -mt-10">
    Key <span className="text-blue-600">Stats</span>
  </h2>

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
    
    <div className="bg-white rounded-xl p-6 shadow-md text-center hover:-translate-y-1 transition">
      <Store className="w-10 h-10 text-blue-600 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-900">1,000+</h3>
      <p className="text-gray-600 text-sm mt-1">
        Pharmacies across Tamil Nadu
      </p>
    </div>

    <div className="bg-white rounded-xl p-6 shadow-md text-center hover:-translate-y-1 transition">
      <Building className="w-10 h-10 text-cyan-600 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-900">50–75%</h3>
      <p className="text-gray-600 text-sm mt-1">
        Cost savings on medicines
      </p>
    </div>

    <div className="bg-white rounded-xl p-6 shadow-md text-center hover:-translate-y-1 transition">
      <ShieldCheck className="w-10 h-10 text-teal-600 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-900">100%</h3>
      <p className="text-gray-600 text-sm mt-1">
        Quality-tested procurement
      </p>
    </div>

    <div className="bg-white rounded-xl p-6 shadow-md text-center hover:-translate-y-1 transition">
      <MapPin className="w-10 h-10 text-blue-700 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-900">All Districts</h3>
      <p className="text-gray-600 text-sm mt-1">
        Central warehouse coverage
      </p>
    </div>

    <div className="bg-white rounded-xl p-6 shadow-md text-center hover:-translate-y-1 transition">
      <Users className="w-10 h-10 text-indigo-600 mx-auto mb-4" />
      <h3 className="text-2xl font-bold text-gray-900">Hundreds</h3>
      <p className="text-gray-600 text-sm mt-1">
        Pharmacists & cooperatives empowered
      </p>
    </div>

  </div>
</section>

{/* Store Locations */}
      <section className="w-full px-6 sm:px-10 lg:px-20 py-14">
        <h2 className="text-2xl font-semibold mb-6 text-gray-800">
          Store Locations
        </h2>
        <div className="bg-white rounded-lg shadow-md border overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[60%_40%]">
            {/* Map Section */}
            <div className="h-[400px] lg:h-[500px] relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d125384.30132503736!2d76.95318444999999!3d11.0168445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba859af2f971cb5%3A0x2fc1c81e183ed282!2sCoimbatore%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Fair Price Shop Locations"
              ></iframe>
            </div>

            {/* Filter Section */}
            <div className="bg-white p-6 border-l border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">District</h3>
              <select className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-base mb-6">
                <option>Select District</option>
                <option>Coimbatore</option>
                <option>Chennai</option>
                <option>Madurai</option>
                <option>Salem</option>
                <option>Tiruchirappalli</option>
                <option>Tirunelveli</option>
                <option>Erode</option>
                <option>Vellore</option>
              </select>

              <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Block</h3>
              <select className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-base mb-6">
                <option>Select Block</option>
                <option>Primary Cooperative Society</option>
                <option>Urban Cooperative Society</option>
                <option>Rural Cooperative Society</option>
                <option>Consumer Cooperative</option>
              </select>

              <button className="bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 transition-colors w-full text-base font-medium flex items-center justify-center gap-2">
                <MapPin className="w-4 h-4" />
                FILTER
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mudhalvar;